import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, FileSpreadsheetIcon, CheckCircle2Icon, TableIcon, MapPinIcon, FilterIcon, UsersIcon, SearchIcon, TargetIcon, PackageIcon, RefreshCwIcon, BarChart3Icon, FileTextIcon, FileIcon, DownloadIcon, ArrowRightIcon } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie } from 'recharts';
const tableData = [{
  rank: 1,
  name: 'T&T Supermarket',
  locations: 35,
  priority: 'Tier A',
  score: 94
}, {
  rank: 2,
  name: 'H Mart',
  locations: 28,
  priority: 'Tier A',
  score: 91
}, {
  rank: 3,
  name: '99 Ranch Market',
  locations: 22,
  priority: 'Tier A',
  score: 87
}, {
  rank: 4,
  name: 'Galleria Supermarket',
  locations: 12,
  priority: 'Tier B',
  score: 82
}, {
  rank: 5,
  name: 'Nations Fresh Foods',
  locations: 8,
  priority: 'Tier B',
  score: 78
}];
const pieData = [{
  name: 'Tier A',
  value: 42
}, {
  name: 'Tier B',
  value: 33
}, {
  name: 'Tier C',
  value: 25
}];
const PIE_COLORS = ['#3b82f6', '#6366f1', '#cbd5e1'];
const savedFiles = [{
  name: 'scored_leads_ranked.csv',
  meta: '15 accounts · prioritized by fit',
  icon: FileSpreadsheetIcon,
  color: 'text-blue-500',
  bgColor: 'bg-blue-50',
  size: '24 KB'
}, {
  name: 'outreach_draft_tier_a.md',
  meta: '6 personalized emails · ready to send',
  icon: FileTextIcon,
  color: 'text-emerald-500',
  bgColor: 'bg-emerald-50',
  size: '8 KB'
}, {
  name: 'market_analysis_summary.pdf',
  meta: 'Key findings · competitive gaps',
  icon: FileIcon,
  color: 'text-violet-500',
  bgColor: 'bg-violet-50',
  size: '156 KB'
}];
const suggestions = [{
  icon: MapPinIcon,
  label: 'Map their locations'
}, {
  icon: SearchIcon,
  label: 'Dig deeper on a specific chain'
}, {
  icon: UsersIcon,
  label: 'Find contact info for outreach'
}, {
  icon: FilterIcon,
  label: 'Filter by province or category'
}];
export function OpenclawDemo() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5295">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-5296">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-5297">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-bold mb-4 border border-blue-100" data-id="element-5298">
            <SparklesIcon className="w-4 h-4" data-id="element-5299" />
            Product output
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-5300">
            Structured deliverables, ready to act on
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto" data-id="element-5301">
            The agent returns more than a list of leads. It packages the output
            into organized, usable deliverables so teams can review findings,
            prioritize accounts, and move directly into outreach.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-5302">
          {/* Prioritize Card - Full Width */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-12 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 md:p-8" data-id="element-5303">
            <div className="flex items-center gap-4 mb-2" data-id="element-5304">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-5305">
                <TargetIcon className="w-5 h-5" data-id="element-5306" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-5307">
                Prioritize the right accounts
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed pl-[60px]" data-id="element-5308">
              Every lead is evaluated and ranked so your team knows exactly who
              to contact first. Fit scoring based on size, reviews, and online
              presence.
            </p>

            {/* Horizontal layout: Table + Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-id="element-5309">
              {/* Table */}
              <div className="lg:col-span-7 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-5310">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-5311">
                  <div className="flex items-center gap-2" data-id="element-5312">
                    <TableIcon className="w-4 h-4 text-blue-600" data-id="element-5313" />
                    <span className="text-sm font-semibold text-slate-800" data-id="element-5314">
                      Ranked Accounts
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 bg-white px-2 py-0.5 rounded border border-slate-100" data-id="element-5315">
                    5 of 15
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-5316">
                  <table className="w-full text-sm" data-id="element-5317">
                    <thead data-id="element-5318">
                      <tr className="bg-slate-50 text-left" data-id="element-5319">
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-10" data-id="element-5320">
                          #
                        </th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5321">
                          Account
                        </th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center" data-id="element-5322">
                          Locations
                        </th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5323">
                          Priority
                        </th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right" data-id="element-5324">
                          Score
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5325">
                      {tableData.map((row, idx) => <tr key={row.rank} className={`border-t border-slate-50 ${idx === 0 ? 'bg-blue-50/30' : ''}`} data-id="element-5326">
                          <td className="px-4 py-2.5 text-[11px] text-slate-400 font-medium" data-id="element-5327">
                            {row.rank}
                          </td>
                          <td className="px-4 py-2.5" data-id="element-5328">
                            <div className="flex items-center gap-2" data-id="element-5329">
                              <div className={`w-1.5 h-1.5 rounded-full ${row.priority === 'Tier A' ? 'bg-blue-500' : 'bg-slate-300'}`} data-id="element-5330" />
                              <span className="text-[12px] text-slate-800 font-semibold" data-id="element-5331">
                                {row.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-[12px] text-slate-600 text-center" data-id="element-5332">
                            {row.locations}
                          </td>
                          <td className="px-4 py-2.5" data-id="element-5333">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${row.priority === 'Tier A' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-500'}`} data-id="element-5334">
                              {row.priority}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right" data-id="element-5335">
                            <div className="inline-flex items-center gap-1.5" data-id="element-5336">
                              <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5337">
                                <div className={`h-full rounded-full ${row.score >= 90 ? 'bg-blue-500' : 'bg-slate-300'}`} style={{
                              width: `${row.score}%`
                            }} data-id="element-5338" />
                              </div>
                              <span className={`text-[11px] font-bold ${row.score >= 90 ? 'text-blue-600' : 'text-slate-600'}`} data-id="element-5339">
                                {row.score}
                              </span>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Charts Column */}
              <div className="lg:col-span-5 flex flex-col gap-4" data-id="element-5340">
                {/* Bar Chart */}
                <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-5" data-id="element-5341">
                  <div className="flex items-center gap-2 mb-3" data-id="element-5342">
                    <BarChart3Icon className="w-4 h-4 text-blue-500" data-id="element-5343" />
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-5344">
                      Fit Score Comparison
                    </div>
                  </div>
                  <div className="h-[140px] w-full" data-id="element-5345">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5346">
                      <BarChart data={tableData} margin={{
                      top: 5,
                      right: 5,
                      left: -25,
                      bottom: 0
                    }} data-id="element-5347">
                        <XAxis dataKey="name" tick={{
                        fontSize: 9,
                        fill: '#64748b'
                      }} tickLine={false} axisLine={false} tickFormatter={value => value.length > 8 ? `${value.substring(0, 8)}…` : value} data-id="element-5348" />
                        <YAxis tick={{
                        fontSize: 9,
                        fill: '#94a3b8'
                      }} tickLine={false} axisLine={false} domain={[60, 100]} data-id="element-5349" />
                        <Tooltip cursor={{
                        fill: '#f1f5f9'
                      }} contentStyle={{
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '11px'
                      }} itemStyle={{
                        color: '#0f172a',
                        fontWeight: 600
                      }} labelStyle={{
                        color: '#64748b',
                        fontSize: '10px',
                        marginBottom: '4px'
                      }} data-id="element-5350" />
                        <Bar dataKey="score" radius={[5, 5, 0, 0]} maxBarSize={32} data-id="element-5351">
                          {tableData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.priority === 'Tier A' ? '#3b82f6' : '#cbd5e1'} data-id="element-5352" />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Pie Chart */}
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-5 flex items-center gap-5" data-id="element-5353">
                  <div className="w-[90px] h-[90px] shrink-0" data-id="element-5354">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5355">
                      <PieChart data-id="element-5356">
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={24} outerRadius={42} paddingAngle={3} dataKey="value" stroke="none" data-id="element-5357">
                          {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} data-id="element-5358" />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1" data-id="element-5359">
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2.5" data-id="element-5360">
                      Tier Breakdown
                    </div>
                    <div className="space-y-2" data-id="element-5361">
                      {pieData.map((d, i) => <div key={i} className="flex items-center justify-between" data-id="element-5362">
                          <div className="flex items-center gap-2" data-id="element-5363">
                            <div className="w-2.5 h-2.5 rounded-sm" style={{
                          backgroundColor: PIE_COLORS[i]
                        }} data-id="element-5364" />
                            <span className="text-[11px] text-slate-600 font-medium" data-id="element-5365">
                              {d.name}
                            </span>
                          </div>
                          <span className="text-[11px] font-bold text-slate-700" data-id="element-5366">
                            {d.value}%
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Deliverables Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 md:p-8 flex flex-col" data-id="element-5367">
            <div className="flex items-center gap-4 mb-2" data-id="element-5368">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-5369">
                <PackageIcon className="w-5 h-5" data-id="element-5370" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-5371">
                Get usable deliverables
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed pl-[60px]" data-id="element-5372">
              Structured files ready to review, share, or import into other
              tools.
            </p>

            <div className="flex-1" data-id="element-5373">
              {/* Folder header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-t-xl border border-slate-100 border-b-0" data-id="element-5374">
                <div className="flex items-center gap-1.5" data-id="element-5375">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-5376" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-300" data-id="element-5377" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-200" data-id="element-5378" />
                </div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5379">
                  Working folder
                </span>
              </div>
              <div className="bg-white rounded-b-xl border border-slate-100 divide-y divide-slate-50" data-id="element-5380">
                {savedFiles.map((file, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -8
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: 0.15 + i * 0.08
              }} className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50/50 transition-colors group" data-id="element-5381">
                    <div className={`w-9 h-9 rounded-lg ${file.bgColor} flex items-center justify-center shrink-0`} data-id="element-5382">
                      <file.icon className={`w-4 h-4 ${file.color}`} data-id="element-5383" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5384">
                      <div className="text-[12px] font-semibold text-slate-700 font-mono truncate" data-id="element-5385">
                        {file.name}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5" data-id="element-5386">
                        {file.meta}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" data-id="element-5387">
                      <span className="text-[10px] text-slate-300 font-medium" data-id="element-5388">
                        {file.size}
                      </span>
                      <div className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5389">
                        <DownloadIcon className="w-3 h-3 text-emerald-600" data-id="element-5390" />
                      </div>
                      <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-5391" />
                    </div>
                  </motion.div>)}
              </div>
              <div className="mt-2.5 flex items-center justify-between px-1" data-id="element-5392">
                <span className="text-[10px] text-slate-400" data-id="element-5393">
                  3 files · 188 KB total
                </span>
                <span className="text-[10px] font-medium text-emerald-600 flex items-center gap-1" data-id="element-5394">
                  All exports complete
                  <CheckCircle2Icon className="w-3 h-3" data-id="element-5395" />
                </span>
              </div>
            </div>
          </motion.div>

          {/* Continue Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 md:p-8 flex flex-col" data-id="element-5396">
            <div className="flex items-center gap-4 mb-2" data-id="element-5397">
              <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0" data-id="element-5398">
                <RefreshCwIcon className="w-5 h-5" data-id="element-5399" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-5400">
                Continue the workflow
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed pl-[60px]" data-id="element-5401">
              Pick up where you left off. Extend any workflow without starting
              over.
            </p>

            <div className="flex-1" data-id="element-5402">
              <div className="bg-slate-50 rounded-xl border border-slate-100 p-5" data-id="element-5403">
                {/* Agent message */}
                <div className="flex items-start gap-3 mb-4" data-id="element-5404">
                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5405">
                    <SparklesIcon className="w-4 h-4 text-violet-600" data-id="element-5406" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 border border-slate-100 shadow-sm" data-id="element-5407">
                    <p className="text-[13px] text-slate-700 leading-relaxed font-medium" data-id="element-5408">
                      Here are some next steps I can help with:
                    </p>
                  </div>
                </div>

                {/* Suggestion chips - 2 column grid */}
                <div className="grid grid-cols-2 gap-2.5 pl-11" data-id="element-5409">
                  {suggestions.map((s, i) => <motion.button key={i} initial={{
                  opacity: 0,
                  y: 6
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.25,
                  delay: 0.25 + i * 0.06
                }} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50 transition-all text-left group shadow-sm" data-id="element-5410">
                      <div className="w-7 h-7 rounded-lg bg-slate-50 group-hover:bg-white border border-slate-100 flex items-center justify-center shrink-0" data-id="element-5411">
                        <s.icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-violet-600" data-id="element-5412" />
                      </div>
                      <span className="text-[12px] font-medium text-slate-700 group-hover:text-violet-700" data-id="element-5413">
                        {s.label}
                      </span>
                    </motion.button>)}
                </div>

                {/* Input hint */}
                <div className="mt-3 pl-11" data-id="element-5414">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-slate-200 bg-white/60" data-id="element-5415">
                    <span className="text-[11px] text-slate-400 italic" data-id="element-5416">
                      Or type your own follow-up...
                    </span>
                    <div className="ml-auto flex gap-0.5" data-id="element-5417">
                      <div className="w-1 h-1 rounded-full bg-slate-300 animate-pulse" data-id="element-5418" />
                      <div className="w-1 h-1 rounded-full bg-slate-300 animate-pulse" style={{
                      animationDelay: '0.2s'
                    }} data-id="element-5419" />
                      <div className="w-1 h-1 rounded-full bg-slate-300 animate-pulse" style={{
                      animationDelay: '0.4s'
                    }} data-id="element-5420" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}